import "./App.css";
import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { Backdrop } from "./components/main/Backdrop";
import { Content } from "./components/main/Content";
import { Log } from "./components/main/Log";
import { useEffect, useState } from "react";
import { push, ref, serverTimestamp, update } from "firebase/database";
import "firebase/compat/auth";
import "firebase/compat/analytics";
import { SignIn } from "./components/main/SignIn";
import { TopNavigation, ITopNavigationItem } from "./components/navigation/TopNavigation";
import { Footer } from "./components/navigation/Footer";
import { getContent, selectContent } from "./features/app/contentSlice";
import { useAppSelector, useAppDispatch } from "./app/hooks";
import { auth, database, tracker } from "./firebase/firebase";

function App() {
  const dispatch = useAppDispatch();
  const content = useAppSelector(selectContent);

  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isBooted, setIsBooted] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    dispatch(getContent());

    const unregisterAuthObserver = auth.onAuthStateChanged((user) => {
      setIsSignedIn(user != null);
      setIsBooted(true);
    });

    return () => unregisterAuthObserver();
  }, [dispatch]);

  useEffect(() => {
    const fonts = (document as any).fonts;
    if (!fonts?.ready) {
      setFontsReady(true);
      return;
    }
    let cancelled = false;
    fonts.ready.then(() => {
      if (!cancelled) setFontsReady(true);
    });
    const fallback = window.setTimeout(() => {
      if (!cancelled) setFontsReady(true);
    }, 1500);
    return () => {
      cancelled = true;
      window.clearTimeout(fallback);
    };
  }, []);

  function handleSubmit(next: string) {
    const user = auth.currentUser;
    if (!user) return;

    const updates: Record<string, unknown> = {
      data: {
        value: next,
        uid: user.uid,
        user: user.displayName ?? "anonymous",
        postedAt: serverTimestamp(),
      },
    };

    // Archive the prior phrase only when it carries the new schema (uid
    // present). Pre-migration entries lack uid and would fail the history
    // mirror validation, so they're left in place.
    if (content.value && content.uid && content.user && content.postedAt) {
      const archiveKey = push(ref(database, "history")).key;
      if (archiveKey) {
        updates[`history/${archiveKey}`] = {
          value: content.value,
          uid: content.uid,
          user: content.user,
          postedAt: content.postedAt,
        };
      }
    }

    update(ref(database), updates);
  }

  function navigationItems(): ITopNavigationItem[] {
    if (!isBooted) return [];

    const path = location.pathname;
    const items: ITopNavigationItem[] = [
      { name: "Phrase", onClick: () => navigate("/"), current: path === "/" },
      { name: "Log", onClick: () => navigate("/log"), current: path === "/log" },
    ];
    if (isSignedIn) {
      items.push({
        name: "Logout",
        utility: true,
        onClick: () => {
          auth.signOut();
          navigate("/");
        },
      });
    } else {
      items.push({
        name: "Sign in",
        utility: true,
        onClick: () => navigate("/signin"),
        current: path === "/signin",
      });
    }
    return items;
  }

  const displayName = auth.currentUser?.displayName ?? undefined;

  return (
    <div className={`App${fontsReady ? " fonts-ready" : ""}`}>
      <div className="grain" aria-hidden="true" />
      <Backdrop />
      <TopNavigation
        items={navigationItems()}
        user={displayName}
        onBrandClick={() => navigate("/")}
      />

      <Routes>
        <Route
          path="/"
          element={
            <Content
              isSignedIn={isSignedIn}
              onSubmit={handleSubmit}
              value={content.value}
              user={content.user}
              postedAt={content.postedAt}
            />
          }
        />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/log" element={<Log />} />
        <Route
          path="/*"
          element={
            <Content
              isSignedIn={false}
              value={"No no no no"}
              user={location.pathname}
            />
          }
        />
      </Routes>

      <Footer tracker={tracker} />
    </div>
  );
}

export default App;
